import React, { useRef } from "react";
import {
  CampaignSideNavStep,
  PropertyStatus,
  PropertyType,
} from "../layout/CampaignBuilderSideNav";
import { useTranslation } from "react-i18next";
import { CampaignBuilderSideNavHeader } from "../layout/CampaignBuilderSideNavHeader";
import { CampainBuilderNextBtn } from "../buttons/CampainBuilderNextBtn";
import {
  InputAdornment,
  SelectChangeEvent,
  TextField,
  Theme,
  useMediaQuery,
} from "@mui/material";
import { useLanguageTheme } from "../../../../hooks/useLanguageTheme";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../../store/store";
import {
  setPropertyDescription,
  setPropertyPrice,
  setPropertyStatus,
  setPropertyTitle,
  setPropertyType,
} from "../../../../store/features/campaignBuilderSlice";
import { formatPrice } from "../../../../helpers/dataConverts";
import { AutocompleteComponent } from "../../../_shared/GoogleLoacationSearchCampaignBuider";
import { useResetToDraft } from "../../../../hooks/useResetToDraft";
import {
  MAX_PROPERTY_DESC_LENGTH_ENGLISH,
  MAX_PROPERTY_DESC_LENGTH_HEBREW,
  MAX_PROPERTY_TITLE_LENGTH_ENGLISH,
  MAX_PROPERTY_TITLE_LENGTH_HEBREW,
} from "../../constants";
import { CampaingBuilderSelect } from "../selects/CampaingBuilderSelect";
import { BackToMenuBtn } from "../buttons/BackToMenuBtn";
import { useContextMenu } from "react-contexify";
import AiContextMenu from "../../ai-chat/AiContextMenu";
import { CampainBuilderBackBtn } from "../buttons/CampainBuilderBackBtn";

export const textFieldStyles = (fontFamily: string, error?: any) => ({
  backgroundColor: "rgba(232, 239, 242, 1)",
  borderRadius: "16px",
  border: error
    ? "1px solid rgba(250, 99, 99, 1)"
    : "1px solid rgba(5, 57, 87, 0.46)",
  fontSize: "14px",
  boxSizing: "border-box",
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      border: "none",
      boxSizing: "border-box",
    },
  },
  "& .MuiInputBase-input::placeholder": {
    color: "rgba(131, 138, 158, 1)",
    opacity: 1,
    fontWeight: 500,
    fontFamily: fontFamily,
  },
  "& .MuiInputBase-input": {
    color: "rgba(0, 0, 0, 1)",
    fontWeight: 500,
    fontFamily: fontFamily,
    padding: "25px 20px",
    fontSize: "14px",
  },
  "& .MuiInputBase-input.MuiInputBase-inputMultiline": {
    padding: "0",
  },
});

export const textFieldStyles2 = (fontFamily: string, error?: any) => ({
  backgroundColor: "rgba(232, 239, 242, 1)",
  borderRadius: "16px",
  border: error
    ? "1px solid rgba(250, 99, 99, 1)"
    : "1px solid rgba(5, 57, 87, 0.46)",
  fontSize: "14px",
  boxSizing: "border-box",
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      border: "none",
      boxSizing: "border-box",
    },
  },
  "& .MuiInputBase-input::placeholder": {
    color: "rgba(131, 138, 158, 1)",
    opacity: 1,
    fontWeight: 500,
    fontFamily: fontFamily,
  },
  "& .MuiInputBase-input": {
    color: "rgba(0, 0, 0, 1)",
    fontWeight: 500,
    fontFamily: fontFamily,
    padding: "15px 20px",
    fontSize: "14px",
  },
  "& .MuiInputBase-input.MuiInputBase-inputMultiline": {
    padding: "0",
  },
});

export interface Errors {
  title: string;
  description: string;
  price: string;
  location: string;
  aboutPropertyDesc: string;
  aboutDeveloper: string;
}

export const MainInformationStep = ({
  setCurrentStep,
  allFieldsErrors,
  setAllFieldsErrors,
  autosaveFunc = () => {},
  isDisabledSwitchLang = false,
}: {
  setCurrentStep: (step: CampaignSideNavStep) => void;
  allFieldsErrors: any;
  setAllFieldsErrors: any;
  autosaveFunc: any;
  isDisabledSwitchLang: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation(["campaignBuilder"]);
  const downMd = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const langTheme = useLanguageTheme();
  const textFieldRef = useRef(null);
  const {
    currency,
    propertyTitle,
    propertyDescription,
    propertyStatus,
    propertyPrice,
    propertyLocation,
    propertyType,
  } = useSelector((state: any) => state.campaignBuilder);

  const resetToDraft = useResetToDraft();

  const [errors, setErrors] = React.useState<any>({});

  const validateFields = () => {
    let errors: Partial<Errors> = {};

    if (propertyTitle.trim() === "") {
      errors.title = t("errors.title_is_required");
    }
    // if (propertyDescription.trim() === "") {
    //   errors.description = t("errors.desc_is_required");
    // }

    if (propertyPrice.trim() === "" || propertyPrice <= 0) {
      errors.price = t("errors.price_is_required");
    }

    if (propertyLocation.address == "") {
      errors.location = t("errors.location_is_required");
    }

    return errors;
  };

  React.useEffect(() => {
    if (allFieldsErrors.main_information) {
      const validationErrors = validateFields();

      setErrors(validationErrors);
    }
  }, []);

  React.useEffect(() => {
    const newAllErrors = { ...allFieldsErrors };

    if (Object.keys(errors).length === 0) {
      delete newAllErrors.main_information;
    } else {
      newAllErrors.main_information = "main_information";
    }
    setAllFieldsErrors(newAllErrors);
  }, [errors]);

  const currentLanguage = i18n.language;
  const isRTL = currentLanguage === "he";

  const handleChangePropertyStatus = (
    event: SelectChangeEvent<PropertyStatus>
  ) => {
    dispatch(setPropertyStatus(event.target.value as PropertyStatus));
    resetToDraft();
    localStorage.setItem("onChangeCompanyBuilder", "1");
  };

  const handleChangePropertyType = (
    event: SelectChangeEvent<PropertyStatus>
  ) => {
    localStorage.setItem("onChangeCompanyBuilder", "1");
    dispatch(setPropertyType(event.target.value as PropertyType));
    resetToDraft();
  };

  const handleChangePropertyTitle = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    localStorage.setItem("onChangeCompanyBuilder", "1");
    const newErrors = { ...errors };

    const value = event.target.value;
    dispatch(setPropertyTitle(value));
    resetToDraft();

    if (!value.trim()) {
      newErrors.title = t("errors.title_is_required");
    } else {
      delete newErrors.title;
    }
    setErrors(newErrors);
  };

  const handleChangePropertyDesc = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    localStorage.setItem("onChangeCompanyBuilder", "1");
    const newErrors = { ...errors };

    const value = event.target.value;
    dispatch(setPropertyDescription(value));
    resetToDraft();

    if (!value.trim()) {
      newErrors.description = t("errors.desc_is_required");
    } else {
      delete newErrors.description;
    }
    setErrors(newErrors);
  };

  const handleChangePropertyPrice = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newErrors = { ...errors };
    localStorage.setItem("onChangeCompanyBuilder", "1");

    let value = event.target.value.replace(/,/g, "");

    const isValidNumber = /^\d*$/.test(value);

    if (isValidNumber) {
      if (value.length > 1 && value.startsWith("0")) {
        value = value.replace(/^0+/, "");
      }

      if (!value || Number(value) <= 1000000000) {
        dispatch(setPropertyPrice(value));
        resetToDraft();
        delete newErrors.price;
      } else {
        newErrors.price = t("errors.price_exceeds_limit");
      }
    } else {
      newErrors.price = t("errors.only_numbers");
    }

    if (!value.trim()) {
      newErrors.price = t("errors.price_is_required");
    }

    setErrors(newErrors);
  };

  const handleNext = (step: CampaignSideNavStep) => {
    const validationErrors = validateFields();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setCurrentStep(step);
    } else {
      setCurrentStep(step);
    }
  };

  const { show } = useContextMenu({ id: "aiFieldContextMenu" });
  const displayMenu = (
    e: any,
    key: string,
    value: any,
    hover: any,
    Hight: any
  ) => {
    const offsetX = currentLanguage === "he" ? 230 : 0;
    show({
      event: e,
      props: {
        key: key,
        value: value,
      },
      position: hover
        ? {
            x: Math.max(e.clientX - offsetX, 0),
            y: e.clientY,
          }
        : {
            x: currentLanguage === "he" ? 0 : window.innerWidth - 240,
            y: textFieldRef.current
              ? (textFieldRef.current as HTMLTextAreaElement)?.clientHeight +
                  Hight || 0
              : 0,
          },
    });
  };
  // currentLanguage === "en" ? "he" : "en",

  return (
    <div className="campaign-builder-side-nav-wrapper">
      <CampaignBuilderSideNavHeader
        header="main_information"
        leftBtn={
          <CampainBuilderBackBtn
            setCurrentStep={setCurrentStep}
            step={CampaignSideNavStep.settings}
          />
        }
        rightBtn={
          <CampainBuilderNextBtn
            handleNext={handleNext}
            step={CampaignSideNavStep.media}
          />
        }
      />
      <AiContextMenu />

      <div
        className="campaign-builder-sidenav-main-content-wrapper"
        style={{
          paddingTop: downMd ? "30px" : "40px",
          direction: i18n.dir(),
        }}
      >
        <div className="campaign-builder-sidenav-settings-fileds-container">
          <div className="campaign-builder-sidenav-settings-fileds-item">
            <div className="campaign-builder-sidenav-field-name">
              {t("title_of_property_plug")}
            </div>

            <TextField
              fullWidth
              inputRef={textFieldRef}
              placeholder={t("title")}
              value={propertyTitle}
              onChange={handleChangePropertyTitle}
              onContextMenu={(e) =>
                displayMenu(e, "propertyTitle", propertyTitle, true, 80)
              }
              onMouseEnter={(e) =>
                displayMenu(e, "propertyTitle", propertyTitle, false, 80)
              }
              sx={textFieldStyles2(langTheme.mainFont(), errors.title)}
              autoComplete="off"
              inputProps={{
                maxLength: isRTL
                  ? MAX_PROPERTY_TITLE_LENGTH_HEBREW
                  : MAX_PROPERTY_TITLE_LENGTH_ENGLISH,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    sx={{
                      height: "100%",
                      ml: 0,
                      maxHeight: "none",
                      marginRight: isRTL ? "-14px" : "0",
                      pl: isRTL ? "20px" : "0px",
                      pr: isRTL ? "-14px" : "-14px",
                      color: "rgba(0, 0, 0, 1)",
                    }}
                    position="start"
                  >
                    <div
                      className="campaign-builder-sidenav-number-of-char"
                      style={{
                        fontFamily: langTheme.mainFont(),
                      }}
                    >
                      {propertyTitle.length}/
                      {isRTL
                        ? MAX_PROPERTY_TITLE_LENGTH_HEBREW
                        : MAX_PROPERTY_TITLE_LENGTH_ENGLISH}
                    </div>
                  </InputAdornment>
                ),
              }}
            />
            {errors.title && (
              <div className="campaign-builder-errors">{errors.title}</div>
            )}
          </div>

          {/* <div className="campaign-builder-sidenav-settings-fileds-item">
            <div className="campaign-builder-sidenav-field-name">
              {t("description_of_property_plug")}
            </div>

            <div style={{ position: "relative" }}>
              <TextField
                fullWidth
                inputRef={textFieldRef}
                multiline
                rows={4}
                placeholder={t("description")}
                sx={{
                  ...textFieldStyles(langTheme.mainFont(), errors.description),
                  "& .MuiInputBase-root": {
                    alignItems: "flex-start",
                    gap: "3px",
                    paddingBottom: "40px",
                  },
                  "& textarea": {
                    scrollbarWidth: "thin",
                    "&::-webkit-scrollbar": {
                      width: "3px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "transparent",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "gray",
                      borderRadius: "4px",
                    },
                  },
                }}
                autoComplete="off"
                value={t(propertyDescription)}
                onChange={handleChangePropertyDesc}
                onContextMenu={(e) =>
                  displayMenu(e, "propertyDescription", propertyDescription,true,275)
                }
                onMouseEnter={(e) => displayMenu(e, "propertyTitle", propertyDescription,false,275)}
                inputProps={{
                  maxLength: isRTL
                    ? MAX_PROPERTY_DESC_LENGTH_HEBREW
                    : MAX_PROPERTY_DESC_LENGTH_ENGLISH,
                }}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: "13px",
                  right: isRTL ? "auto" : "27px",
                  left: isRTL ? "27px" : "auto",
                  fontFamily: "DM Sans",
                  color: "#838A9E",
                  padding: "2px 4px",
                  borderRadius: "4px",
                  pointerEvents: "none",
                  fontSize: "14px",
                }}
              >
                {propertyDescription.length}/
                {isRTL
                  ? MAX_PROPERTY_DESC_LENGTH_HEBREW
                  : MAX_PROPERTY_DESC_LENGTH_ENGLISH}
              </div>
            </div>

            {errors.description && (
              <div className="campaign-builder-errors">
                {errors.description}
              </div>
            )}
          </div> */}

          <div className="campaign-builder-sidenav-settings-fileds-item">
            <div className="campaign-builder-sidenav-field-name">
              {t("full_address")}
            </div>

            <AutocompleteComponent errors={errors} setErrors={setErrors} />
            {errors.location && (
              <div className="campaign-builder-errors">{errors.location}</div>
            )}
          </div>

          <div className="campaign-builder-sidenav-settings-fileds-item">
            <div className="campaign-builder-sidenav-field-name">
              {t("price")}
            </div>
            <TextField
              value={formatPrice(propertyPrice)}
              onChange={handleChangePropertyPrice}
              fullWidth
              placeholder={t("price")}
              sx={textFieldStyles2(langTheme.mainFont(), errors.price)}
              autoComplete="off"
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    sx={{
                      height: "100%",
                      ml: 0,
                      maxHeight: "none",
                      marginRight: isRTL ? "0px" : "-14px",
                      marginLeft: isRTL ? "-14px" : "0",
                      pl: isRTL ? "0px" : "6px",
                      pr: isRTL ? "20px" : "0px",
                      color: "rgba(0, 0, 0, 1)",
                    }}
                    position="start"
                  >
                    <div>{t(currency)[0]}</div>
                  </InputAdornment>
                ),
              }}
            />
            {errors.price && (
              <div className="campaign-builder-errors">{errors.price}</div>
            )}
          </div>

          <div className="campaign-builder-sidenav-settings-fileds-item">
            <div className="campaign-builder-sidenav-field-name">
              {t("property_status")}
            </div>

            <CampaingBuilderSelect
              value={propertyStatus}
              onChangeValue={handleChangePropertyStatus}
              options={Object.values(PropertyStatus)}
            />
          </div>

          <div className="campaign-builder-sidenav-settings-fileds-item">
            <div className="campaign-builder-sidenav-field-name">
              {t("property_type")}
            </div>

            <CampaingBuilderSelect
              value={propertyType}
              onChangeValue={handleChangePropertyType}
              options={Object.values(PropertyType)}
            />
          </div>
        </div>

        <BackToMenuBtn setCurrentStep={setCurrentStep} />
      </div>
    </div>
  );
};
